// Load the existing vendor bootstrap only when a quote uploader is needed.
// App.setup is Cloudlift's documented custom-placement API; never reset a mounted field.
let appPromise;
const mounts = new WeakMap();

export function validUploadScript(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'assets.cloudlift.app'
      && url.pathname === '/api/assets/upload.js' && !url.username && !url.password
      && /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(url.searchParams.get('shop') || '')
      ? url.href : null;
  } catch { return null; }
}

function loadApp(scriptUrl, timeoutMs) {
  const src = validUploadScript(scriptUrl);
  if (!src) return Promise.reject(new Error('Invalid upload bootstrap'));
  const current = window.Cloudlift?.upload?.App;
  if (typeof current?.setup === 'function') return Promise.resolve(current);
  if (appPromise) return appPromise;
  appPromise = new Promise((resolve, reject) => {
    let script = [...document.scripts].find(item => item.src === src);
    if (script?.dataset.vadorUploadFailed === 'true') { script.remove(); script = null; }
    let poll;
    const finish = (error, app) => {
      clearInterval(poll); clearTimeout(timeout);
      script?.removeEventListener('error', failed);
      if (error) {
        if (error.message === 'Upload bootstrap failed' && script?.dataset.vadorUploadLoader === 'true') {
          script.dataset.vadorUploadFailed = 'true';
        }
        reject(error);
      } else resolve(app);
    };
    const failed = () => finish(new Error('Upload bootstrap failed'));
    const timeout = setTimeout(() => finish(new Error('Upload bootstrap timed out')), timeoutMs);
    poll = setInterval(() => {
      const app = window.Cloudlift?.upload?.App;
      if (typeof app?.setup === 'function') finish(null, app);
    }, 100);
    if (!script) {
      script = document.createElement('script');
      script.src = src; script.async = true; script.dataset.vadorUploadLoader = 'true';
      script.addEventListener('error', failed, {once:true});
      document.head.append(script);
    } else script.addEventListener('error', failed, {once:true});
  }).catch(error => { appPromise = null; throw error; });
  return appPromise;
}

export function ensureQuoteUploads(options) {
  const {container, signal} = options;
  if (signal?.aborted) return Promise.reject(new DOMException('Upload view disconnected', 'AbortError'));
  if (mounts.has(container)) return mounts.get(container);
  const pending = mountQuoteUploads(options).finally(() => mounts.delete(container));
  mounts.set(container, pending);
  return pending;
}

async function mountQuoteUploads({container, scriptUrl, signal, timeoutMs = 15000}) {
  const aborted = () => new DOMException('Upload view disconnected', 'AbortError');
  if (signal?.aborted) throw aborted();
  if (container.querySelector('input[type="file"]')) return;
  // A newly loaded bootstrap runs its own setup. Starting another setup before
  // that asynchronous mount finishes can create two fields on native products.
  const appAlreadyLoaded = typeof window.Cloudlift?.upload?.App?.setup === 'function';
  const app = await loadApp(scriptUrl, timeoutMs);
  if (signal?.aborted) throw aborted();
  if (container.querySelector('input[type="file"]')) return;
  await new Promise((resolve, reject) => {
    const finish = error => {
      observer.disconnect(); clearTimeout(timeout); signal?.removeEventListener('abort', cancel);
      if (error) reject(error); else resolve();
    };
    const cancel = () => finish(aborted());
    const observer = new MutationObserver(() => {
      if (container.querySelector('input[type="file"]')) finish();
    });
    const timeout = setTimeout(() => finish(new Error('Upload field timed out')), timeoutMs);
    observer.observe(container, {childList:true, subtree:true});
    signal?.addEventListener('abort', cancel, {once:true});
    try {
      // Only reinitialize preloaded apps for new markup; preserve any mounted field.
      if (appAlreadyLoaded && !container.querySelector('.cl-upload--wrapper')) app.setup();
      if (container.querySelector('input[type="file"]')) finish();
    } catch (error) { finish(error); }
  });
}
